import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { ThemeProvider } from '@mui/material/styles';
import { CustomScalarSpreadsheet } from 'components/CustomScalarSpreadsheet';
import {
  WORKBOARD_SPREADSHEET_SKELETON_ROW_COUNT,
  WORKBOARD_SPREADSHEET_VIRTUAL_ROW_HEIGHT,
} from 'dashboard409a/common/constants/workboardSpreadsheet';
import useWorkboardSortKeyDownCapture from 'dashboard409a/hooks/useWorkboardSortKeyDownCapture';
import useWorkboardSpreadsheetLayout from 'dashboard409a/hooks/useWorkboardSpreadsheetLayout';
import lightTheme from 'theme';
import { WorkboardSpreadsheetViewProps } from './WorkboardSpreadsheetView.props';

const WorkboardSpreadsheetSkeleton = ({ columnCount }: { columnCount: number }) => (
  <Box sx={{ backgroundColor: '#fff', flex: 1, minHeight: 0, overflow: 'hidden', p: 1 }}>
    <Skeleton variant="rectangular" height={36} sx={{ mb: 0.5, borderRadius: 0.5 }} />
    {Array.from({ length: WORKBOARD_SPREADSHEET_SKELETON_ROW_COUNT }).map((_, i) => (
      <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
        {Array.from({ length: Math.max(columnCount, 1) }).map((__, j) => (
          <Skeleton
            key={j}
            variant="rectangular"
            height={34}
            sx={{ flex: 1, borderRadius: 0.5, opacity: 1 - i * 0.06 }}
          />
        ))}
      </Box>
    ))}
  </Box>
);

const BASE_CONTAINER_SX = {
  backgroundColor: '#fff',
  flex: 1,
  minHeight: 0,
  // Datasheet owns scrolling when virtualized.
  overflow: 'hidden',
  '& .data-grid': {
    width: '100%',
    tableLayout: 'fixed',
  },
  // Rows must render at exactly VIRTUAL_ROW_HEIGHT: the padder rows reserve
  // rowHeight per row, so shorter cells leave blank space at the end of the
  // scroll range. `td.cell` is required to outweigh the datasheet's own
  // `.data-grid-container .data-grid .cell` height.
  '& .data-grid td.cell': {
    boxSizing: 'border-box',
    height: `${WORKBOARD_SPREADSHEET_VIRTUAL_ROW_HEIGHT}px`,
  },
  '& .data-grid td.workboard-row-click-cell': {
    cursor: 'pointer',
  },
  // Opaque fills so sticky header / first column do not show scrolled content through.
  // pinnedRowCount: 2 includes a display:none legend at row 0; library sets
  // top = rowIndex * rowHeight (50px) on the real header — pin it flush instead.
  '& .data-grid td.table-header': {
    backgroundColor: '#f1f5f9',
    top: '0 !important',
  },
  '& .data-grid td:first-of-type': {
    backgroundColor: '#fff',
  },
  '& .data-grid tr.stripe-row--even td:first-of-type': {
    backgroundColor: '#f1f5f9',
  },
  '& .data-grid td.table-header:first-of-type': {
    backgroundColor: '#f1f5f9',
    zIndex: 4,
  },
  // Lift default 175px max-width on notes only — do not use !important so
  // saved/resized inline column widths still win.
  '& .data-grid td.workboard-notes-cell': {
    maxWidth: 'none',
    padding: 0,
    textAlign: 'left',
    overflow: 'hidden',
    '& .MuiInput-root': {
      maxWidth: 'none',
    },
  },
  '& .data-grid td.workboard-empty-message-cell': {
    color: 'text.secondary',
    fontStyle: 'italic',
    textAlign: 'left',
  },
  '& #spreadsheet-table__title-columns': {
    display: 'none',
  },
  '& #spreadsheet-table__content-columns': {
    maxWidth: '100% !important',
    width: '100%',
  },
} as const;

const WorkboardSpreadsheetView = ({
  visibleColumnIds,
  columnWidths,
  isReady,
  sheet,
  cells,
  workbook,
  onChange,
  onColumnResize,
  onColumnReorder,
  onSheetClick,
  onRequestSort,
  extraContainerSx,
}: WorkboardSpreadsheetViewProps) => {
  const { containerRef, columnWidthsForSpreadsheet, virtualization, isViewportReady } =
    useWorkboardSpreadsheetLayout({
      visibleColumnIds,
      columnWidths,
    });

  const handleSortKeyDownCapture = useWorkboardSortKeyDownCapture(onRequestSort);

  const showSheet = Boolean(isReady && sheet && isViewportReady && virtualization);

  return (
    <ThemeProvider theme={lightTheme}>
      <Box
        ref={containerRef}
        onClick={showSheet ? onSheetClick : undefined}
        onKeyDownCapture={showSheet ? handleSortKeyDownCapture : undefined}
        sx={{ ...BASE_CONTAINER_SX, ...extraContainerSx }}>
        {!showSheet || !sheet || !virtualization ? (
          <WorkboardSpreadsheetSkeleton columnCount={visibleColumnIds.length} />
        ) : (
          <CustomScalarSpreadsheet
            isLoading={false}
            data={(sheet.data as any) ?? []}
            onChange={onChange as any}
            sheet={sheet as any}
            columns={sheet.columns as any}
            rowConfig={sheet.rowConfig as any}
            cells={cells as any}
            workbook={workbook as any}
            showTitlesColumn={false}
            showTotalColumn={false}
            displayRowIndicator={false}
            displayColumnIndicator={false}
            alwaysDisplayLegend={false}
            columnWidths={columnWidthsForSpreadsheet}
            columnTypes={visibleColumnIds}
            onColumnReorder={onColumnReorder}
            onColumnResize={onColumnResize}
            virtualization={virtualization}
            disablePerfectScrollbar
            isReadOnly
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default WorkboardSpreadsheetView;
